// The shared ledger shape: a tab is a titled table, a row carries what it
// prints and what it sorts by, and a selection is what the left rail reads.
// The same shape drives Map Review's table, Operations' ledger, and Planning's.
//
// Filtering is a function over rows plus a declaration, never a predicate
// written in a page: a tab states its `LedgerFilter`s, `filterRows` applies
// them, and `clauseLabel` names what is narrowing. One rule set for every
// ledger, and a server-backed page reads the same declaration when it
// translates the values into a query.

export interface LedgerColumn {
	key: string;
	label: string;
	align?: "start" | "end";
	width?: string;
}

export interface LedgerRow {
	/** The object type this row selects; the page owns what each means. */
	kind: string;
	id: number;
	swatch?: string;
	cells: string[];
	sort: (string | number | null)[];
}

export interface LedgerTab {
	key: string;
	label: string;
	badgeCount: number;
	columns: LedgerColumn[];
	rows: LedgerRow[];
	defaultSort: { key: string; descending: boolean };
}

export interface LedgerSelection {
	kind: string;
	id: number;
}

/** The stable key a multi-selection holds. The kind is part of it because a
 * Lead and a Signal can carry the same numeric id. */
export function ledgerRowKey(row: { kind: string; id: number }): string {
	return `${row.kind}:${row.id}`;
}

/**
 * The declaration of one filter: what it is called, what kind of value it
 * takes, and which cell it reads. A page adds a filter by adding one of these —
 * the matching control and the predicate come from the kind, so no page writes
 * its own `row => row.type === …`.
 *
 * `column` is the index into `LedgerRow.cells` (and `LedgerRow.sort`, which is
 * parallel to the tab's `columns`), because a row is what the filter narrows
 * and the cells already carry the printed text.
 */
export interface LedgerFilter {
	key: string;
	label: string;
	kind: "enum" | "text" | "number" | "date" | "boolean";
	/** The `enum` vocabulary, in the order the control offers it. */
	options?: { value: string; label: string }[];
	column: number;
}

/** A `number` or `date` filter's bounds. A number filter's are numbers; a date
 * filter's are the cell's own `YYYY-MM-DD` vocabulary, which is why a bound is
 * either. A missing or blank bound is open on that side. */
export interface LedgerRangeValue {
	min?: number | string | null;
	max?: number | string | null;
}

/** What one filter is set to. `null` is "not set"; a range carries `min`/`max`
 * for `number` and `date`; `enum`, `text` and a single date bound is a string;
 * `boolean` is the boolean the cell's own `true`/`false` text is compared to. */
export type LedgerFilterValue = string | boolean | LedgerRangeValue | null;

/** One applied clause, as the bar's chips list it. */
export interface LedgerClause {
	key: string;
	label: string;
}

/** The whole of a `YYYY-MM-DD`-prefixed date cell: the day, not whatever the
 * row prints after it (`2026-01-31 09:12` compares as `2026-01-31`). */
const DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

/** The day part of a cell that begins with a date, or `null` when it does not
 * begin with one — a cell with no date cannot satisfy a date bound. */
function cellDay(cell: string): string | null {
	const text = cell.trim();
	return DATE_PREFIX.test(text) ? text.slice(0, 10) : null;
}

/** A cell read as a measure. The blank cell is absent, not zero. */
function cellNumber(cell: string): number | null {
	const text = cell.trim();
	if (text === "") return null;
	const number = Number(text);
	return Number.isFinite(number) ? number : null;
}

/** A lower/upper bound as a number, from either a number or the digits an
 * input's string holds. Anything else — a blank, `NaN`, text — is open. */
function numberBound(bound: number | string | null | undefined): number | null {
	if (typeof bound === "number") return Number.isFinite(bound) ? bound : null;
	if (typeof bound === "string" && bound.trim() !== "") {
		const number = Number(bound);
		return Number.isFinite(number) ? number : null;
	}
	return null;
}

/** A lower/upper date bound as `YYYY-MM-DD` text, open when blank. Truncated to
 * the day so a bound and a cell are compared in the same vocabulary. */
function dayBound(bound: number | string | null | undefined): string | null {
	if (typeof bound !== "string") return null;
	const text = bound.trim();
	return text === "" ? null : text.slice(0, 10);
}

/** Whether a value narrows anything: `null`, a blank string and a range with
 * neither bound are all "not set", so a cleared control never hides rows. */
function isSet(
	value: LedgerFilterValue | undefined,
): value is string | boolean | LedgerRangeValue {
	if (value === null || value === undefined) return false;
	if (typeof value === "string") return value.trim() !== "";
	if (typeof value === "boolean") return true;
	return (
		numberBound(value.min) !== null ||
		dayBound(value.min) !== null ||
		numberBound(value.max) !== null ||
		dayBound(value.max) !== null
	);
}

/** One filter's predicate, with its bounds and needles resolved once instead of
 * per row. `null` when the value is unset, so the caller can skip it entirely. */
function compile(
	filter: LedgerFilter,
	value: LedgerFilterValue,
): ((row: LedgerRow) => boolean) | null {
	if (!isSet(value)) return null;
	const column = filter.column;
	switch (filter.kind) {
		case "enum": {
			// The vocabulary's own members, matched exactly: the option the
			// operator picked is the cell text the row prints.
			const wanted = String(value);
			return (row) => (row.cells[column] ?? "") === wanted;
		}
		case "text": {
			// A typed filter narrows as it is typed, so it is a substring, and
			// case does not matter to an operator reading a cell.
			const needle = String(value).trim().toLowerCase();
			return (row) => (row.cells[column] ?? "").toLowerCase().includes(needle);
		}
		case "boolean": {
			const wanted = String(value).trim().toLowerCase();
			return (row) => (row.cells[column] ?? "").trim().toLowerCase() === wanted;
		}
		case "number": {
			const range = value as LedgerRangeValue;
			const min = numberBound(range.min);
			const max = numberBound(range.max);
			return (row) => {
				const number = cellNumber(row.cells[column] ?? "");
				if (number === null) return false;
				if (min !== null && number < min) return false;
				if (max !== null && number > max) return false;
				return true;
			};
		}
		case "date": {
			const range = value as LedgerRangeValue;
			const min = dayBound(range.min);
			const max = dayBound(range.max);
			// Inclusive: a bound is the day the operator picked, and an
			// exclusive compare would drop that day's rows from "after".
			return (row) => {
				const day = cellDay(row.cells[column] ?? "");
				if (day === null) return false;
				if (min !== null && day < min) return false;
				if (max !== null && day > max) return false;
				return true;
			};
		}
	}
}

/**
 * The rows every set filter keeps — AND across filters, since two clauses on one
 * ledger are two statements about the same row. An unset filter keeps
 * everything, and a ledger with no set filter gets its own rows back untouched.
 */
export function filterRows(
	rows: LedgerRow[],
	filters: LedgerFilter[],
	values: Record<string, LedgerFilterValue>,
): LedgerRow[] {
	const predicates: ((row: LedgerRow) => boolean)[] = [];
	for (const filter of filters) {
		const predicate = compile(filter, values[filter.key] ?? null);
		if (predicate !== null) predicates.push(predicate);
	}
	if (predicates.length === 0) return rows;
	return rows.filter((row) => predicates.every((keeps) => keeps(row)));
}

/**
 * A tab's rows as the drawer shows them: first the drawn area's membership, then
 * every set filter clause.
 *
 * The area narrows by the ids the draw selected rather than by geometry — a
 * ledger row carries no shape, so the selection the map produced IS the honest
 * reading of "inside it". Order matters only for what the count means: both are
 * AND, so a clause can only take rows away from the area, never add one back.
 */
export function narrowRows(
	rows: LedgerRow[],
	filters: LedgerFilter[],
	values: Record<string, LedgerFilterValue>,
	areaKeys: Set<string> | null = null,
): LedgerRow[] {
	const scoped = areaKeys
		? rows.filter((row) => areaKeys.has(ledgerRowKey(row)))
		: rows;
	return filterRows(scoped, filters, values);
}

/** A chip's text: what is narrowing, in the operator's words. `null` when the
 * value is unset, which is what keeps the chips and the table in agreement. */
export function clauseLabel(
	filter: LedgerFilter,
	value: LedgerFilterValue,
): string | null {
	if (!isSet(value)) return null;
	switch (filter.kind) {
		case "enum": {
			const member = filter.options?.find(
				(option) => option.value === String(value),
			);
			return `${filter.label}: ${member?.label ?? String(value)}`;
		}
		case "text":
			return `${filter.label}: ${String(value).trim()}`;
		case "boolean":
			return `${filter.label}: ${String(value).trim() === "true" ? "yes" : "no"}`;
		case "number": {
			const range = value as LedgerRangeValue;
			const min = numberBound(range.min);
			const max = numberBound(range.max);
			if (min === null && max === null) return null;
			if (min !== null && max !== null) return `${filter.label} ${min}–${max}`;
			if (min !== null) return `${filter.label} ≥ ${min}`;
			return `${filter.label} ≤ ${max}`;
		}
		case "date": {
			const range = value as LedgerRangeValue;
			const min = dayBound(range.min);
			const max = dayBound(range.max);
			if (min === null && max === null) return null;
			if (min !== null && max !== null) return `${filter.label} ${min}–${max}`;
			if (min !== null) return `${filter.label} after ${min}`;
			return `${filter.label} before ${max}`;
		}
	}
}

/** The clauses in force, in declaration order. The bar's chips and a page's
 * "is anything narrowing" question both read this one list. */
export function activeClauses(
	filters: LedgerFilter[],
	values: Record<string, LedgerFilterValue>,
): LedgerClause[] {
	const clauses: LedgerClause[] = [];
	for (const filter of filters) {
		const label = clauseLabel(filter, values[filter.key] ?? null);
		if (label !== null) clauses.push({ key: filter.key, label });
	}
	return clauses;
}
